/**
 * Simplified WebSocket Notification Hook
 *
 * Features:
 * - Single connection per page load
 * - No automatic reconnection (user refreshes page to reconnect)
 * - Uses custom events to notify components
 * - Minimal complexity, no dependency chains
 */

import { useState, useEffect, useRef } from 'react';

const useNotificationWebSocket = () => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Small delay to ensure component is fully mounted and token is available
    const initTimer = setTimeout(() => {
      // Check if still mounted
      if (!mountedRef.current) return;

      // Get JWT token
      const token = localStorage.getItem('token');

      // Guard against empty or missing token
      if (!token || token.trim() === '') {
        console.log('[WebSocket] Waiting for authentication - no token available yet');
        return;
      }

      // Prevent duplicate connections
      if (wsRef.current &&
          (wsRef.current.readyState === WebSocket.CONNECTING ||
           wsRef.current.readyState === WebSocket.OPEN)) {
        console.log('[WebSocket] Connection already exists, skipping');
        return;
      }

    // Create WebSocket URL - derive from REACT_APP_BACKEND_URL or current window location
    const backendUrl = process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'http://localhost:8000'
      ? process.env.REACT_APP_BACKEND_URL
      : `${window.location.protocol}//${window.location.host}`;
    const wsUrl = backendUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
    const url = `${wsUrl}/ws/notifications?token=${encodeURIComponent(token)}`;

    console.log('[WebSocket] Connecting to:', url.replace(/token=[^&]+/, 'token=***'));

    // Create WebSocket connection
    let ws = null;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Failed to create WebSocket:', error);
      return;
    }

    ws.onopen = () => {
      console.log('[WebSocket] Connected successfully');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[WebSocket] Message received:', data.type);

        if (data.type === 'notification') {
          // Dispatch custom event for components to listen
          console.log('[WebSocket] New notification:', data.payload);
          window.dispatchEvent(new CustomEvent('new-notification', {
            detail: data.payload
          }));
        } else if (data.type === 'connected') {
          console.log('[WebSocket] Connection confirmed:', data.connection_id);
        }
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      setConnected(false);
    };

    ws.onclose = (event) => {
      console.log('[WebSocket] Disconnected:', event.code, event.reason);
      setConnected(false);

      // Don't automatically reconnect - user can refresh page
      if (event.code !== 1000) {
        console.log('[WebSocket] Connection lost. Please refresh the page to reconnect.');
      }
    };

    }, 100); // 100ms delay to ensure stable mount

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      clearTimeout(initTimer);
      console.log('[WebSocket] Cleaning up connection');
      if (wsRef.current &&
          (wsRef.current.readyState === WebSocket.OPEN ||
           wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, []); // Empty dependency array - only run once on mount

  return { connected };
};

export default useNotificationWebSocket;
